<template>
  <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
  <EditableComponent
    v-else-if="isEditMode && !isViewOnlyMode"
    :id="id"
    :active="componentEditMode && componentEditMode?.id === id"
    :class="{ 'mt-12': componentName }"
    :edit="isEditable"
    :exclude-stylables="[ThemeComponentOptionEnum.COLOR_PICKER]"
    :remove="isRemovable"
    :selected="componentSelectedMode && componentSelectedMode?.id === id"
    settings
    stylable
    :style="omit(style, 'paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight')"
    @edit="handleEdit"
    @remove="removeComponentById(id)"
    @select="setComponentSelectedMode(id)"
    @settings="setComponentSettingsMode(id)"
    @style="emit('style', $event)"
  >
    <ContentListRaw
      :id="id"
      ref="contentListRef"
      class="pa-2"
      :class="props.class"
      :data="data"
      v-bind="attrs"
      :has-section-selector-app="!!sectionSelectorAppSrc"
      :print-mode-idx="printModeIdx"
      :section-list-name="componentName"
      :should-edit-with-app="shouldEditWithApp"
      :style="pick(style, 'backgroundColor', 'paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight')"
      @browse-with-app-clicked="handleEdit"
      @just-added="handleEdit"
    />
  </EditableComponent>
  <ContentListRaw
    v-else
    :id="id"
    :data="data"
    v-bind="attrs"
    :has-section-selector-app="!!sectionSelectorAppSrc"
    preview-mode
    :print-mode-idx="printModeIdx"
    :section-list-name="componentName"
    :should-edit-with-app="shouldEditWithApp"
    :style="!isEditMode ? style : undefined"
    @browse-with-app-clicked="handleEdit"
  />

  <iframe
    v-if="isEditMode && isImpact && !isViewOnlyMode && isAppModalOpen && sectionSelectorAppSrc"
    ref="iframeRef"
    class="fixed inset-0 w-full h-full border-none z-100"
    :src="sectionSelectorAppSrc"
  />
</template>

<script setup lang="ts">
import { computed, ref, inject, useAttrs, nextTick, toRefs, type ComputedRef, type CSSProperties } from 'vue'
import { omit, pick, cloneDeep } from 'lodash-es'

import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import ContentListRaw from './ContentList.raw.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasCompletionWizard from '@canvas-builder/composables/useCanvasCompletionWizard'
import { ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import type {
  ComponentNode,
  ComponentNodePrint,
  ComponentNodeLinkable,
  ContentListSection,
} from '@canvas-builder/types/canvas'
import type { ContentListProps } from '@canvas-builder/types/canvas'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import type { AppFile } from '@lib/types/app'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { doesLocalOverrideExist, getLocalOverrideUrl } from '@lib/util/dev.util'
import { ComponentTypes } from '@canvas-builder/types/canvas'
import { waitForIframeInitialize } from '@lib/utils/iframeInitialize'
import type { FileContentTypeEnum, CanvasRetrieve } from '@openapi'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    data: ContentListProps
    tracking_id: string
    visible?: ComponentNode['visible']
    allow_admins_to_overwrite?: boolean
    style?: CSSProperties
    printModeIdx?: number
    class?: string | string[] | object
    print?: ComponentNodePrint
    autofill?: 'off' | 'ai' | 'matching_metadata' | 'matching_tags' | 'matching_metadata_or_tags'
    linkable?: ComponentNodeLinkable
    autofill_content_types?: FileContentTypeEnum[] | null
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: CanvasRetrieve['id']
  }>(),
  {
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  style: [s: CSSProperties]
}>()
const sectionSelectorApps = inject<ComputedRef<AppFile[]>>(
  'sectionSelectorApps',
  computed(() => []),
)
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { isCompletionWizardEnabled, retriggerWizard } = useCanvasCompletionWizard()

const {
  isImpact,
  mode,
  isEditMode,
  componentSelectedMode,
  isViewOnlyMode,
  componentEditMode,
  componentNodesById,
  activeCanvas,
  saveCanvasContentWithContext,
  removeComponentById,
  setComponentSelectedMode,
  setComponentSettingsMode,
  updateNodeDataById,
  canvasContent,
} = useCanvas()

// Get refs from props for useComponentPermissions
const { id, usedInSectionId, when_used_in_section } = toRefs(props)

// Get base permissions from the permission system (respects template permissions)
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

// Combine template permissions with selection_strategy logic
// In Impact mode, selection_strategy can further restrict permissions
const isEditable = computed(() => {
  // First check template permissions
  if (!componentPermissions.value.canEdit) return false

  // Then apply selection_strategy restrictions (Impact mode only)
  if (isImpact.value) {
    return (
      !props.data.selection_strategy ||
      props.data.selection_strategy === 'free' ||
      (isCompletionWizardEnabled.value && wizardStepSectionListIds.value.includes(props.id))
    )
  }

  return true
})

const isRemovable = computed(() => {
  // First check template permissions
  if (!componentPermissions.value.canRemove) return false

  // Then apply selection_strategy restrictions (Impact mode only)
  if (isImpact.value) {
    return !props.data.selection_strategy || props.data.selection_strategy === 'free'
  }

  return true
})
const sectionSelectorAppSrc = computed(() => {
  if (!sectionSelectorApps.value?.length || !props.data.selection_app_name) return null
  const relatedApp = sectionSelectorApps.value.find((app) => app.app_metadata?.name === props.data.selection_app_name)
  if (!relatedApp) return null
  try {
    const appName = relatedApp.app_metadata?.name
    if (appName && doesLocalOverrideExist(appName)) {
      return getLocalOverrideUrl(appName)
    }
  } catch (e) {
    console.error('Error generating Embeddable debug url', e)
  }

  return relatedApp.content_url || null
})

const shouldEditWithApp = computed(() => !!isImpact.value && !!sectionSelectorAppSrc.value)
const attrs = useAttrs()

const contentListRef = ref<typeof ContentListRaw | null>(null)

const componentName = computed(() => {
  if (!launchDarkly.value?.display_section_list_name) return

  const node = componentNodesById.value[props.id] as ComponentNode
  return node.tracking_id !== node.type ? node.tracking_id : ''
})

const isAppModalOpen = ref(false)

const iframeRef = ref<HTMLIFrameElement | null>(null)

const wizardStepSectionListIds = computed(() => {
  // Compute an array of section list IDs that require the wizard
  return canvasContent.value.reduce(
    (acc, node) => {
      if (node.type !== ComponentTypes.ContentList) return acc

      const data = node.data as ContentListProps
      if (data.selection_strategy === 'free') return acc
      if (data.selection_strategy === 'app' && data.selection_app_name) {
        const appExists = sectionSelectorApps.value.some(
          (app) =>
            app.app_metadata?.name === data.selection_app_name &&
            (app.content_url || (app.app_metadata?.name && doesLocalOverrideExist(app.app_metadata.name))),
        )
        if (!appExists) return acc
      }

      if (data.selection_strategy) {
        acc.push(node.id)
      }

      return acc
    },
    [] as ComponentNode['id'][],
  )
})

async function handleEdit() {
  if (!isImpact.value) {
    contentListRef.value?.toggleSectionSelector()
    return
  }

  if (isCompletionWizardEnabled.value && wizardStepSectionListIds.value.includes(props.id)) {
    await retriggerWizard({ sectionListId: props.id })
  } else if (shouldEditWithApp.value) {
    isAppModalOpen.value = true

    // Wait for iframe to be mounted
    await nextTick()

    waitForIframeInitialize(
      iframeRef.value,
      {
        ...props.data,
        canvas_id: activeCanvas?.value?.id,
        section_list_id: props.id,
        initial_section_ids: activeCanvas.value?.context?.pitcher?.completion_wizard?.initial_section_list_data?.[
          props.id
        ]?.map((section: { id: string }) => section.id),
      },
      'section-selector',
      async (selectedData?: { section_ids?: string[] | ContentListSection[]; form_data?: Record<string, any> }) => {
        console.info(`[SectionList]: Received done callback for ${props.id} with data`, selectedData)
        if (!selectedData) return
        const sections = (selectedData?.section_ids || []).map((id) => ({ id: typeof id === 'string' ? id : id.id }))
        const updatedData = { ...props.data, sections }
        updateNodeDataById(props.id, updatedData)
        const currentContext = cloneDeep(activeCanvas.value?.context || {})
        await saveCanvasContentWithContext(
          {
            ...currentContext,
            wizard: {
              ...currentContext?.wizard,
              ...selectedData.form_data,
            },
          },
          {
            showToast: false,
          },
        ).catch((e) => {
          console.warn('[SectionList] Failed to save canvas content with context', e)
        })
        isAppModalOpen.value = false
      },
      { componentName: 'ContentList' },
    )
  } else {
    contentListRef.value?.toggleSectionSelector()
  }
}
</script>
